// Queue or push an event. Also, push queued events.
import type { DataLayerInput } from '@corratech/pylot-tag-manager'
import type { MenuTrackingData } from '@corratech/pylot-tag-manager/src/hooks/useMenuTracking'
import { GTMEvent, PageViewData } from '../GTM'
import { EventQueueData, getQueueOrPushEvent } from '../hooks/useEventQueue'
import { GTMEvents } from '../GTMEvents'
import { NextRouter } from 'next/router'
export const getEventDataConsumer = (
    eventQueue: EventQueueData,
    getStoredData: () => PageViewData,
    addStoredData: (data: PageViewData) => void,
    resetStoredData: () => void,
    router: NextRouter
): (({ type, data }: DataLayerInput) => void) => {
    const {
        eventHistory,
        resetQueue,
        checkQueueBeforeEvent,
        checkQueueAfterEvent,
        dequeueNextPageEvent
    } = eventQueue
    const queueOrPushEvent = getQueueOrPushEvent(
        checkQueueBeforeEvent,
        checkQueueAfterEvent
    )
    return ({ type, data }: DataLayerInput) => {
        let preparedData: GTMEvent | undefined
        switch (type) {
            case 'PDP':
                preparedData = GTMEvents.PDP(data)
                break
            case 'ADD_TO_CART':
                GTMEvents.CART_STATE(data, addStoredData)
                preparedData = GTMEvents.ADD_TO_CART(data, router)
                break
            case 'REMOVE_FROM_CART':
                GTMEvents.CART_STATE(data, addStoredData)
                preparedData = GTMEvents.REMOVE_FROM_CART(data, router)
                break
            case 'PLP':
                preparedData = GTMEvents.PLP(data)
                break
            case 'PRODUCT_CLICK':
                preparedData = GTMEvents.PRODUCT_CLICK(data)
                break
            case 'PROMOTION_CLICK':
                preparedData = GTMEvents.PROMOTION_CLICK(data)
                break
            case 'PROMOTION_VIEW':
                preparedData = GTMEvents.PROMOTION_VIEW(data)
                break
            case 'SEARCH':
                preparedData = GTMEvents.SEARCH(data)
                break
            case 'CART_VIEW':
                preparedData = GTMEvents.CART_VIEW(data)
                break
            case 'CHECKOUT':
                preparedData = GTMEvents.CHECKOUT(data)
                break
            case 'CHECKOUT_OPTION':
                preparedData = GTMEvents.CHECKOUT_OPTION(data)
                break
            case 'PLACE_ORDER':
                preparedData = GTMEvents.PLACE_ORDER(data)
                break
            case 'MENU_CLICK':
                preparedData = GTMEvents.MENU_CLICK(data as MenuTrackingData)
                break
            case 'APPLY_COUPON':
                preparedData = GTMEvents.APPLY_COUPON(data)
                break
            case 'TRIGGER_IMMEDIATELY':
                dequeueNextPageEvent(data.eventName)
                break
            // The events below are not being tracked immediately (do not return data for push).
            // They provide data, needed for pageview event, via addStoredData hook.
            case 'USER_STATE':
            case 'USER_LOGIN':
            case 'USER_LOGOUT':
                GTMEvents.USER_STATE(data, addStoredData)
                break
            case 'CART_STATE':
                GTMEvents.CART_STATE(data, addStoredData)
                break
            case 'PAGE_VIEW':
                // This event happens right after visiting a new page
                resetQueue()
                resetStoredData()
                GTMEvents.PAGE_VIEW(data, addStoredData)
                break
            case 'PAGE_VIEW_DATA':
                // Additional pageView data. No need to reset dataLayer
                GTMEvents.PAGE_VIEW_DATA(data, addStoredData)
                break
            default:
                // Push the data directly if it doesn't match any handlers
                if (data) {
                    preparedData = { event: '', data: data }
                }
                break
        }

        // Main data push
        if (preparedData) {
            queueOrPushEvent(preparedData)
        }

        // Track pageview event if possible
        const pageViewData = GTMEvents.PUSH_PAGE_VIEW(
            eventHistory,
            getStoredData
        )
        queueOrPushEvent(pageViewData)

        // Page type events follow right after pageview events
        const pageTypeData = GTMEvents.PUSH_PAGE_TYPE(pageViewData)
        queueOrPushEvent(pageTypeData)
    }
}
